// Carry-forward rules for a group of drawing parts that all mark ONE printed figure (plot C, then mark
// D on the line BC). Shared by StudentPaperTake, StudentMockExamTake and StudentPaperReview so the
// three surfaces agree on the one rule that matters:
//
//   a given/context point whose label an EARLIER part is asked to plot is that part's ANSWER — render
//   the student's own mark for it, or nothing at all, never the authored position.
//
// Grading is untouched by any of this: the server still grades against the full authored spec.
import { normalizeQuestionType } from "../../../utils/fillQuestionUtils";
import { interactiveArrayOf, interactiveValueOf } from "./interactiveAnswerValue";

export const interactiveSpecOf = (question) => question?.answer_data?.interactive || null;

// A `construction` answer is built on its own calibrated board, not on a figure: the question's image is
// a rough sketch that must stay visible in the stem, so the canvas claims no image at all.
export const interactiveImageOf = (question) =>
  interactiveSpecOf(question)?.kind === "construction" ? ""
  : interactiveSpecOf(question)?.image_url
  || (Array.isArray(question?.image_urls) ? question.image_urls.find(Boolean) : null)
  || question?.image_url
  || "";

export const interactiveImgKey = (u) => String(u || "").split("?")[0].split("/").pop();

// Grid-coord point placements a student made on one drawing sub-part, each [gx, gy, label?]. Cut-drag
// and object-shaped answers carry no plottable points, so they contribute nothing.
export const placedGridPointsOf = (subAnswer) => {
  const arr = interactiveArrayOf(subAnswer);
  return Array.isArray(arr) ? arr.filter((p) => Array.isArray(p) && p.length >= 2 && !Array.isArray(p[0])) : [];
};

// Only plotting kinds build on each other's marks. Parts that each work a fresh copy of the same
// picture (shade this fraction, then that one; draw a symbol here, then there) must keep their own
// canvas — sharing one would show a single figure and leave every other part with no image at all.
// `glyph` is here because the parts genuinely mark ONE printed figure — "mark a reflex angle with R"
// then "mark the acute angle with A" on the same shape — which is how the paper prints it; a part that
// really needs a fresh copy of the picture opts out with `separate_grid`.
// `marker` is here for the same reason: "write a scale on the vertical axis" and "complete the bar
// chart" are two parts of ONE printed chart, and the second is answered off the first's scale.
export const SHARED_GRID_KINDS = ["points", "drag-shape", "polygon", "rect", "reflect", "rotate", "lines", "glyph", "marker"];

// A part plots on the group's grid whenever it carries a plotting spec — its question TYPE need not be
// "drawing". "Plot the other three vertices on the grid above, and write down their co-ordinates" is a
// multi-fill that also plots; excluding it gave that part a second canvas of the same printed grid.
export const plotsOnSharedGrid = (s) =>
  !!interactiveSpecOf(s) && SHARED_GRID_KINDS.includes(interactiveSpecOf(s)?.kind);

export const sharedGridDrawingSubs = (subQuestions) => {
  const draws = (subQuestions || []).filter(plotsOnSharedGrid);
  if (draws.length < 2) return [];
  if (draws.some((s) => interactiveSpecOf(s)?.separate_grid)) return [];
  const key0 = interactiveImgKey(interactiveImageOf(draws[0]));
  if (!key0) return [];
  return draws.every((s) => interactiveImgKey(interactiveImageOf(s)) === key0) ? draws : [];
};

// The typed boxes of a fill part live alongside its drawing, so its answer travels as
// { blanks, interactive } — the drawn half has to be read out of / written back into that wrapper.
export const isFillWithGrid = (s) => plotsOnSharedGrid(s) && normalizeQuestionType(s) !== "drawing";

export const gridValueOf = (sub, subAnswer) =>
  isFillWithGrid(sub)
    ? interactiveValueOf(subAnswer && typeof subAnswer === "object" && !Array.isArray(subAnswer) ? subAnswer.interactive : [])
    : interactiveValueOf(subAnswer);

// The group's grid geometry, taken from whichever part authored it. Every part of a shared grid draws on
// the SAME printed lattice, but a `lines` part ("draw on any lines of symmetry") is authored with
// fractional coordinates and often carries no `grid` at all. The canvas then fell back to its default
// 0.1 spacing, so the earlier parts' work — stored in grid coordinates — was painted at a completely
// different place on the picture: opening part (e) appeared to move the shape the student had drawn.
// The answers a page holds for its parts: a map keyed by sub id on the past-paper pages, or a function
// on pages that key their answers some other way (the mock exam falls back to `sub_<idx>` for a part
// with no id).
const answerFor = (answers, sub) => (typeof answers === "function" ? answers(sub) : answers?.[sub?.id]);
// Identity by id where there is one — a part with no id is only ever itself.
const isSameSub = (a, b) => (a?.id != null && b?.id != null ? a.id === b.id : a === b);

export const sharedGridGeometry = (parts) => {
  for (const s of parts || []) {
    const g = interactiveSpecOf(s)?.grid;
    if (g && Number(g.dx) && Number(g.dy)) return g;
  }
  return null;
};

// …but a lattice may only be borrowed from a part drawn on the SAME printed figure. Q20 of North London
// 2013 is three unrelated pictures in one group — (a) draw the lines of symmetry of a cross, (b) reflect
// a shape on its own dot grid, (c) add a square to a third grid — and (a), having no grid of its own,
// inherited (b)'s. Every tap then snapped to a lattice that does not exist on (a)'s picture: the cross's
// vertical axis falls between two of those columns, so the correct answer could not be drawn at all and
// four accurately aimed lines were all marked wrong.
const gridSourceParts = (subQuestions, targetSub) => {
  const key = interactiveImgKey(interactiveImageOf(targetSub));
  return (subQuestions || []).filter(
    (s) => plotsOnSharedGrid(s) && (!key || interactiveImgKey(interactiveImageOf(s)) === key)
  );
};

// Where an EARLIER shared-grid part put the point it names `label`. Three outcomes, and the caller
// needs all three apart: `undefined` — no earlier part plots that label, so whatever the current part
// says about it is genuinely printed on the paper; `null` — an earlier part owns it but the student
// has not placed it yet; `[gx, gy]` — the student's own mark, right or wrong.
export const sharedGridPlacedForLabel = (subQuestions, safeAnswers, targetSub, label) => {
  if (!label) return undefined;
  const targetKey = interactiveImgKey(interactiveImageOf(targetSub));
  for (const s of subQuestions) {
    if (isSameSub(s, targetSub)) break;
    if (!plotsOnSharedGrid(s)) continue;
    if (interactiveImgKey(interactiveImageOf(s)) !== targetKey) continue;
    const sLabels = Array.isArray(interactiveSpecOf(s)?.point_labels) ? interactiveSpecOf(s).point_labels : [];
    const li = sLabels.indexOf(label);
    if (li === -1) continue;
    const placed = placedGridPointsOf(gridValueOf(s, answerFor(safeAnswers, s)));
    return placed[li] ? [placed[li][0], placed[li][1]] : null;
  }
  return undefined;
};

// A reflect/rotate guide's original_shape can include a vertex the student only just plotted in an
// EARLIER part of the same grouped question (e.g. part b plots C, part c reflects triangle ABC) —
// the authored original_shape bakes in C's correct position so the guide would leak part b's answer
// the moment part c is opened. original_shape_labels (parallel array, entries null or a label like
// "C") flags which vertices are actually derived from an earlier part rather than printed on the
// diagram: those are swapped for the student's own placed point (right or wrong, never the answer),
// or dropped from the guide entirely while that earlier part is still unanswered.
export const sharedGridOriginalShapeGuide = (subQuestions, safeAnswers, targetSub) => {
  const spec = interactiveSpecOf(targetSub);
  const original = Array.isArray(spec?.original_shape) ? spec.original_shape : [];
  // "Reflect shape A and label it B" (part b), then "reflect shape B" (part c): the ghost shape part
  // (c) transforms IS part (b)'s answer, so the server sends no coordinates for the vertices that part
  // owns — only which part owns them. What survives in original_shape is genuinely printed on the
  // paper ("the first point has already been plotted for you"); the rest is the student's own work.
  const sourceQbId = spec?.original_shape_source_qb_id;
  if (sourceQbId) {
    const source = (subQuestions || []).find((s) => Number(s?.qb_id ?? s?.id) === Number(sourceQbId));
    if (!source) return original;
    const placed = placedGridPointsOf(gridValueOf(source, answerFor(safeAnswers, source))).map((p) => [p[0], p[1]]);
    if (!placed.length) return original;
    const merged = [...original.map((p) => [p[0], p[1]]), ...placed];
    // The two halves are in no common order, and the canvas draws the ghost as a polygon in the order
    // given — so a printed corner between two placed ones would draw a bowtie. Sorting by angle about
    // the centroid recovers the outline for a convex figure, which every ghost shape here is (the same
    // ordering the canvas already applies to printed corners).
    const cx = merged.reduce((s, p) => s + p[0], 0) / merged.length;
    const cy = merged.reduce((s, p) => s + p[1], 0) / merged.length;
    return merged.sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  }
  const labels = Array.isArray(spec?.original_shape_labels) ? spec.original_shape_labels : null;
  if (!labels || !original.length) return original;
  return original
    .map((vertex, i) => (labels[i] ? (sharedGridPlacedForLabel(subQuestions, safeAnswers, targetSub, labels[i]) || null) : vertex))
    .filter(Boolean);
};

// The same leak as original_shape, through `given_points`. "C has coordinates (7,1). Mark C" (part b),
// then "D is on the line BC" (part c) — part c is authored with C among its given_points, so opening it
// printed C at the correct place whether or not the student had marked it there, and a student who put
// C somewhere else saw BOTH their C and the answer's C. A given point is only genuinely printed on the
// paper when no earlier part is asked to plot it; otherwise it is that part's answer and must be shown
// as the student's own mark, or not at all until they make one.
export const sharedGridGivenPointsGuide = (subQuestions, safeAnswers, targetSub) => {
  const spec = interactiveSpecOf(targetSub);
  const given = Array.isArray(spec?.given_points) ? spec.given_points : null;
  if (!given || !given.length) return spec?.given_points;
  let changed = false;
  const out = [];
  for (const p of given) {
    const label = (Array.isArray(p) && typeof p[2] === "string") ? p[2] : "";
    const placed = sharedGridPlacedForLabel(subQuestions, safeAnswers, targetSub, label);
    if (placed === undefined) { out.push(p); continue; }
    changed = true;
    if (placed) out.push([placed[0], placed[1], label]);
  }
  return changed ? out : given;
};

// Build the read-only context_points a drawing sub inherits from EARLIER drawing subs that share the
// same background image — so parts that build on one figure (plot A/B/C, then reflect ABC) render on a
// single continuous grid. Only points the student has actually placed are carried forward. The current
// part's own reflect/rotate guide (original_shape) already shows the source shape, so we skip a part's
// context when it already draws that shape itself.
export const sharedGridContextPoints = (subQuestions, safeAnswers, targetSub) => {
  const targetSpec = interactiveSpecOf(targetSub);
  // Each part is a fresh copy of the same printed grid — nothing carries between them.
  if (targetSpec?.separate_grid) return [];
  let targetKey = interactiveImgKey(interactiveImageOf(targetSub));
  // Join/edge parts may keep grid geometry but lose the PNG after figure stripping — inherit
  // the earlier plot part's diagram key so "Join ABCD" still sees A–D.
  if (!targetKey) {
    for (const s of subQuestions) {
      if (isSameSub(s, targetSub)) break;
      if (!plotsOnSharedGrid(s)) continue;
      const k = interactiveImgKey(interactiveImageOf(s));
      if (k) {
        targetKey = k;
        break;
      }
    }
  }
  // Points the target part already draws itself (a reflect/rotate's source shape) aren't repeated.
  // …nor points the part already carries as its own printed/given corners — a carried duplicate would
  // draw a second grey dot on top of an authored, joinable one. Dedup against the RESOLVED guide shape
  // (the one actually rendered by the canvas, per sharedGridOriginalShapeGuide above), not the raw,
  // unresolved spec.original_shape — those coordinates can differ from the vertex an earlier part's
  // student answer resolves to, which let the same vertex slip past this dedup and get drawn a second
  // time as a context point, painting two overlapping shapes.
  const resolvedOriginalShape = sharedGridOriginalShapeGuide(subQuestions, safeAnswers, targetSub);
  const ownShape = [
    ...resolvedOriginalShape,
    ...(sharedGridGivenPointsGuide(subQuestions, safeAnswers, targetSub) || []),
    ...(Array.isArray(targetSpec?.context_points) ? targetSpec.context_points : []),
  ];
  const ownKeys = new Set(ownShape.map((p) => `${Math.round(p[0])},${Math.round(p[1])}`));
  const out = [];
  const seen = new Set(ownKeys);
  for (const s of subQuestions) {
    if (isSameSub(s, targetSub)) break;
    if (!plotsOnSharedGrid(s)) continue;
    const earlierKey = interactiveImgKey(interactiveImageOf(s));
    if (targetKey && earlierKey && earlierKey !== targetKey) continue;
    if (targetKey && !earlierKey) {
      // allow — same inherited figure run
    } else if (!targetKey && earlierKey) {
      continue;
    }
    const spec = interactiveSpecOf(s);
    if (spec?.kind !== "points" && spec?.kind !== "drag-shape" && spec?.kind !== "polygon" && spec?.kind !== "reflect" && spec?.kind !== "rotate") continue;
    const labels = Array.isArray(spec?.point_labels) ? spec.point_labels : [];
    placedGridPointsOf(gridValueOf(s, answerFor(safeAnswers, s))).forEach((p, i) => {
      const key = `${Math.round(p[0])},${Math.round(p[1])}`;
      if (seen.has(key)) return;
      seen.add(key);
      const lbl = (typeof p[2] === "string" && p[2] !== "") ? p[2] : (labels[i] || "");
      out.push(lbl ? [p[0], p[1], lbl] : [p[0], p[1]]);
    });
  }
  return out;
};

// The spec to hand InteractiveDiagramCanvas for `targetSub`: the authored one with every point that
// belongs to an earlier part resolved to the student's own mark, plus the earlier parts' placements as
// read-only joinable context. The base spec's identity is preserved when nothing needed rewriting, so
// the canvas does not see a new spec object on every render.
// `carried` overrides the context points (the take page's unified grid tags them with each part's
// colour); `gridParts` is the set the fallback grid geometry is read from.
export const guidedSpecFor = (subQuestions, safeAnswers, targetSub, { carried, gridParts } = {}) => {
  const baseSpec = interactiveSpecOf(targetSub);
  if (!baseSpec) return baseSpec;
  const context = carried || sharedGridContextPoints(subQuestions, safeAnswers, targetSub);
  const guideShape = sharedGridOriginalShapeGuide(subQuestions, safeAnswers, targetSub);
  const givenGuide = sharedGridGivenPointsGuide(subQuestions, safeAnswers, targetSub);
  const givenRewritten = givenGuide !== baseSpec.given_points;
  const inheritedGrid = baseSpec.grid
    ? null
    : sharedGridGeometry(gridParts || gridSourceParts(subQuestions, targetSub));
  if (!context.length && !baseSpec.original_shape_labels && !baseSpec.original_shape_source_qb_id
    && !inheritedGrid && !givenRewritten) return baseSpec;
  return {
    ...baseSpec,
    ...(inheritedGrid ? { grid: inheritedGrid } : null),
    original_shape: guideShape,
    given_points: givenGuide,
    context_points: [...(baseSpec.context_points || []), ...context],
  };
};

// Construction parts of one question that continue a single figure ("construct the triangle", then
// "measure the angles on your drawing"). A later part names the earlier part's board_id in
// share_board_with. Separate constructions in the same question leave that field empty and keep
// their own boards. `separate_board` is the opt-out, the same role `separate_grid` plays for graphs.
const constructionSpec = (s) => {
  const spec = interactiveSpecOf(s);
  return spec?.kind === "construction" ? spec : null;
};

export const sharedConstructionSubs = (subQuestions) => {
  const draws = (subQuestions || []).filter((s) => constructionSpec(s));
  if (draws.length < 2) return [];
  if (draws.some((s) => constructionSpec(s).separate_board)) return [];
  const boardIds = new Set(draws.map((s) => constructionSpec(s).board_id).filter(Boolean));
  const owners = new Set(
    draws.map((s) => constructionSpec(s).share_board_with).filter((id) => boardIds.has(id))
  );
  if (owners.size === 0) return [];
  const group = draws.filter((s) => {
    const spec = constructionSpec(s);
    return owners.has(spec.board_id) || owners.has(spec.share_board_with);
  });
  return group.length >= 2 ? group : [];
};

// A construction answer is { points, segments, circles, measures }. A written companion wraps it.
export const constructionValueOf = (stored) => {
  const v = stored && typeof stored === "object" && !Array.isArray(stored) && stored.interactive
    && typeof stored.interactive === "object" && !Array.isArray(stored.interactive)
    && !Array.isArray(stored.points)
    ? stored.interactive
    : stored;
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  if (!Array.isArray(v.points) && !Array.isArray(v.segments) && !Array.isArray(v.circles) && !v.measures) return null;
  return v;
};

const constructionGeometryScore = (v) =>
  (Array.isArray(v?.points) ? v.points.length : 0)
  + (Array.isArray(v?.segments) ? v.segments.length : 0)
  + (Array.isArray(v?.circles) ? v.circles.length : 0);

// One board shows the fullest drawing any part of the group holds, with the active part's readings.
export const sharedConstructionBoardValue = (parts, answers, activeSub) => {
  const storedOf = (sub) => (typeof answers === "function" ? answers(sub) : answers?.[sub?.id]);
  let best = null;
  let bestScore = -1;
  for (const s of parts || []) {
    const v = constructionValueOf(storedOf(s));
    const score = constructionGeometryScore(v);
    if (score > bestScore) {
      best = v;
      bestScore = score;
    }
  }
  const active = constructionValueOf(storedOf(activeSub));
  const out = {
    points: Array.isArray(best?.points) ? best.points : [],
    segments: Array.isArray(best?.segments) ? best.segments : [],
    circles: Array.isArray(best?.circles) ? best.circles : [],
  };
  const measures = active?.measures && typeof active.measures === "object" ? active.measures : null;
  if (measures) out.measures = measures;
  return out;
};

export const constructionGeometriesEqual = (a, b) => {
  const pack = (v) => JSON.stringify({
    points: v?.points || [],
    segments: v?.segments || [],
    circles: v?.circles || [],
  });
  return pack(a) === pack(b);
};
